import { ValidationInterface } from '../../validation.interface';
import {
  DigesterPeriodType,
  DigesterType,
  PlantSpecifications, PlantSpecificationsPropName, PlantUnitType,
} from '../../../../models/project/plant-specifications/plant-specifications.model';
import { ProjectValidationUnit } from '../../../../models/data-structures/project-validation';
import { UnitValueValidation } from '../unit-value.validation';
import { ProductValidation } from './product.validation';
import { FuelValidation } from './fuel.validation';
import { AdditionalProductValidation } from './additional_product.validation';
import { EnergyGenPropName } from '../../../../models/project/plant-specifications/energy-generation-unit.model';
import { FuelInterface } from '../../../../models/project/plant-specifications/fuel.model';

export class PlantSpecificationsValidation implements ValidationInterface {
  /** The current plant spec obj that is being validated */
  plantSpec: PlantSpecifications;

  /**
   * Sets the plant spec obj that will be used for validation
   * @param plantSpec will be used to set the current plant spec obj
   * @param isProjectInput is a boolean that determines if a project input form
   * is being validated to make some inputs optional
   */
  constructor(plantSpec: PlantSpecifications, private isProjectInput?: boolean) {
    if (!plantSpec) throw Error('invalid plant spec provided for validation');
    this.plantSpec = new PlantSpecifications(plantSpec);
  }

  /**
   * Get a validation unit containing the validation data for the current plant spec obj
   */
  getValidation(): ProjectValidationUnit {
    if (!this.plantSpec || !this.plantSpec.selectedUnits) throw Error('unable to validate plant spec');
    const validationUnit = new ProjectValidationUnit();
    this.addInitialValidation(validationUnit);
    if (!this.isProjectInput) {
      validationUnit.addModuleResults(this.getDigesterValidation(), PlantSpecValidationSection.DIGESTER);
    }
    if (this.plantSpec.selectedUnits.includes(PlantUnitType.CHP)) {
      validationUnit.addModuleResults(this.getChpValidation(), PlantSpecValidationSection.CHP);
    }
    if (this.plantSpec.selectedUnits.includes(PlantUnitType.UPGRADER)) {
      validationUnit.addModuleResults(this.getUpgraderValidation(), PlantSpecValidationSection.UPGRADER);
    }
    if (this.plantSpec.selectedUnits.includes(PlantUnitType.GENERATOR)) {
      validationUnit.addModuleResults(this.getGeneratorValidation(), PlantSpecValidationSection.GENERATOR);
    }
    if (this.plantSpec.selectedUnits.includes(PlantUnitType.BOILER)) {
      validationUnit.addModuleResults(this.getBoilerValidation(), PlantSpecValidationSection.BOILER);
    }
    if (this.plantSpec.selectedUnits.includes(PlantUnitType.DEWATERING)) {
      validationUnit.addModuleResults(this.getDewateringValidation(), PlantSpecValidationSection.DEWATERING);
    }
    this.addProductValidation(validationUnit);
    this.addAdditionalProductValidation(validationUnit);
    this.addFuelValidation(validationUnit);
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current plant spec obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }

  /**
   * Adds validation information for the initial plant spec validation
   * @param validationUnit is a ProjectValidationUnit that will have the initial plant spec validation added to it
   */
  private addInitialValidation(validationUnit: ProjectValidationUnit): void {
    this.checkPlantSpec();
    if (this.plantSpec.selectedDigester) {
      validationUnit.setValid(PlantSpecificationsPropName.selectedDigester);
    } else {
      validationUnit.setInvalid(PlantSpecificationsPropName.selectedDigester, [PlantSpecError.MISSING_SELECTED_DIGESTER]);
    }
    if (this.plantSpec.selectedUnits && this.plantSpec.selectedUnits.length > 0) {
      validationUnit.setValid(PlantSpecificationsPropName.selectedUnits);
    } else {
      validationUnit.setInvalid(PlantSpecificationsPropName.selectedUnits, [PlantSpecError.MISSING_AT_LEAST_ONE_UNIT]);
    }
  }

  /**
   * Get a ProjectValidationUnit that contains validation information for the digester module
   */
  private getDigesterValidation() {
    this.checkPlantSpec();
    const validationUnit = new ProjectValidationUnit();
    if (this.plantSpec.selectedDigester === DigesterType.DRY_BATCH) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.overheadVolume).getValidation(),
        PlantSpecificationsPropName.overheadVolume
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.periodHRT).getValidation(),
        PlantSpecificationsPropName.periodHRT
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.workingDays).getValidation(),
        PlantSpecificationsPropName.workingDays
      );
    } else if (this.plantSpec.selectedDigester === DigesterType.OTHER) {
      if (this.plantSpec.digesterName) {
        validationUnit.setValid(PlantSpecificationsPropName.digesterName);
      } else {
        validationUnit.setInvalid(PlantSpecificationsPropName.digesterName);
      }
      if (this.plantSpec.periodType === DigesterPeriodType.HRT) {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.plantSpec.periodHRT).getValidation(),
          PlantSpecificationsPropName.periodHRT
        );
      } else {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.plantSpec.periodOLR).getValidation(),
          PlantSpecificationsPropName.periodOLR
        );
      }

    } else {
      validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.overheadVolume).getValidation(),
        PlantSpecificationsPropName.overheadVolume
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.targetTS).getValidation(),
        PlantSpecificationsPropName.targetTS
      );
      if (this.plantSpec.periodType === DigesterPeriodType.HRT) {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.plantSpec.periodHRT).getValidation(),
          PlantSpecificationsPropName.periodHRT
        );
      } else if (this.plantSpec.periodType === DigesterPeriodType.OWV) {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.plantSpec.overallWorkingVolume).getValidation(),
          PlantSpecificationsPropName.overallWorkingVolume
        );
      } else {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.plantSpec.periodOLR).getValidation(),
          PlantSpecificationsPropName.periodOLR
        );
      }
    }
    if (this.plantSpec.digesterEfficiencyEnabled) {

    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.digesterEfficiency).getValidation(),
      PlantSpecificationsPropName.digesterEfficiency
    );
    }
    if (this.plantSpec.digesterTemperatureEnabled) {

      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.digesterTemperature).getValidation(),
        PlantSpecificationsPropName.digesterTemperature
      );
      }
    return validationUnit;
  }

  /**
   * Get a ProjectValidationUnit that contains validation information for the CHP module
   */
  private getChpValidation() {
    this.checkPlantSpec();
    const validationUnit = new ProjectValidationUnit();
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.electricalEfficiency).getValidation(),
      PlantSpecificationsPropName.electricalEfficiency
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.thermalEfficiency).getValidation(),
      PlantSpecificationsPropName.thermalEfficiency
    );
    if (this.plantSpec.chp.nominalCapacityToggle) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.chp.workingPeriod).getValidation(),
        EnergyGenPropName.workingPeriod
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.chp.nominalCapacity).getValidation(),
        EnergyGenPropName.nominalCapacity
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.chp.minimumOutput).getValidation(),
        EnergyGenPropName.minimumOutput
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.chp.heatingValue).getValidation(),
        EnergyGenPropName.heatingValue
      );
    }
    return validationUnit;
  }

  /**
   * Get a ProjectValidationUnit that contains validation information for the upgrader module
   */
  private getUpgraderValidation() {
    this.checkPlantSpec();
    const validationUnit = new ProjectValidationUnit();
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.methaneRecovery).getValidation(),
      PlantSpecificationsPropName.methaneRecovery
    );
    if (this.plantSpec.upgrader.nominalCapacityToggle) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.upgrader.workingPeriod).getValidation(),
        EnergyGenPropName.workingPeriod
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.upgrader.nominalCapacity).getValidation(),
        EnergyGenPropName.nominalCapacity
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.upgrader.minimumOutput).getValidation(),
        EnergyGenPropName.minimumOutput
      );
    }
    return validationUnit;
  }

  /**
   * Get a ProjectValidationUnit that contains validation information for the generator module
   */
  private getGeneratorValidation() {
    this.checkPlantSpec();
    const validationUnit = new ProjectValidationUnit();
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.electricalEfficiency).getValidation(),
      PlantSpecificationsPropName.electricalEfficiency
    );
    if (this.plantSpec.generator.nominalCapacityToggle) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.generator.workingPeriod).getValidation(),
        EnergyGenPropName.workingPeriod
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.generator.nominalCapacity).getValidation(),
        EnergyGenPropName.nominalCapacity
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.generator.minimumOutput).getValidation(),
        EnergyGenPropName.minimumOutput
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.generator.heatingValue).getValidation(),
        EnergyGenPropName.heatingValue
      );
    }
 
    return validationUnit;
  }

  /**
   * Get a ProjectValidationUnit that contains validation information for the boiler module
   */
  private getBoilerValidation() {
    this.checkPlantSpec();
    const validationUnit = new ProjectValidationUnit();
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.thermalEfficiency).getValidation(),
      PlantSpecificationsPropName.thermalEfficiency
    );
    if (this.plantSpec.boiler.nominalCapacityToggle) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.boiler.workingPeriod).getValidation(),
        EnergyGenPropName.workingPeriod
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.boiler.nominalCapacity).getValidation(),
        EnergyGenPropName.nominalCapacity
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.boiler.minimumOutput).getValidation(),
        EnergyGenPropName.minimumOutput
      );
      validationUnit.addModuleResults(
        new UnitValueValidation(this.plantSpec.boiler.heatingValue).getValidation(),
        EnergyGenPropName.heatingValue
      );
    }
    return validationUnit;
  }

  /**
   * Get a ProjectValidationUnit that contains validation information for dewatering
   */
  private getDewateringValidation() {
    this.checkPlantSpec();
    const validationUnit = new ProjectValidationUnit();
    if (this.plantSpec.dewateringType) {
      validationUnit.setValid(PlantSpecificationsPropName.dewateringType);
    } else {
      validationUnit.setInvalid(PlantSpecificationsPropName.dewateringType, [PlantSpecError.MISSING_DEWATERING_TYPE]);
    }
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.cakeDryness).getValidation(),
      PlantSpecificationsPropName.cakeDryness
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.recovery).getValidation(),
      PlantSpecificationsPropName.recovery
    );
    validationUnit.addModuleResults(
      new UnitValueValidation(this.plantSpec.recycle).getValidation(),
      PlantSpecificationsPropName.recycle
    );
    return validationUnit;
  }

  /**
   * Add product validation to the provided ProjectValidationUnit
   */
  private addProductValidation(validationUnit: ProjectValidationUnit) {
    this.checkPlantSpec();
    if (!this.plantSpec.generatedProducts) throw Error('unable to validate generated products');
    if (this.plantSpec.generatedProducts.length < 1) {
      validationUnit.setInvalid(
        PlantSpecValidationSection.PRODUCTS,
        [PlantSpecError.MISSING_AT_LEAST_ONE_PRODUCT]
      );
      validationUnit.invalidFields += 7;
      return validationUnit;
    }
    const products = this.plantSpec.generatedProducts;
    for (let i = 0; i < products.length; i++) {
      validationUnit.addArrayToModuleResults(
        new ProductValidation(this.plantSpec.generatedProducts[i]).getValidation(),
        PlantSpecValidationSection.PRODUCTS,
        i,
        products[i].name
      );
    }
    return validationUnit;
  }


  /**
   * Add additional product validation to the provided ProjectValidationUnit
   */
  private addAdditionalProductValidation(validationUnit: ProjectValidationUnit) {
    this.checkPlantSpec();
    if (!this.plantSpec.additionalProducts) throw Error('unable to validate generated products');
    if (this.plantSpec.additionalProducts.length < 1) {
      validationUnit.setValid(
        PlantSpecValidationSection.ADDITIONAL_PRODUCTS,
      );
      return validationUnit;
    }
    const products = this.plantSpec.additionalProducts;
    for (let i = 0; i < products.length; i++) {
      validationUnit.addArrayToModuleResults(
        new AdditionalProductValidation(this.plantSpec.additionalProducts[i]).getValidation(),
        PlantSpecValidationSection.ADDITIONAL_PRODUCTS,
        i,
        products[i].name
      );
    }
    return validationUnit;
  }

  /**
   * Add fuel validation to the provided ProjectValidationUnit
   */
  private addFuelValidation(validationUnit: ProjectValidationUnit) {
    this.checkPlantSpec();
    if (!this.plantSpec.consumedFuels) throw Error('unable to validate consumed fuels');
    const fuels = this.plantSpec.consumedFuels;
    for (let i = 0; i < fuels.length; i++) {
      validationUnit.addArrayToModuleResults(
        new FuelValidation(fuels[i] as FuelInterface).getValidation(),
        PlantSpecValidationSection.FUELS,
        i,
        fuels[i].name
      );
    }
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current plant spec instance is set
   */
  private checkPlantSpec() {
    if (!this.plantSpec) throw Error('unable to validate plant spec');
  }
}

/**
 * An enum that contains errors that relate to plant spec validation
 */
enum PlantSpecError {
  MISSING_SELECTED_DIGESTER = 'VALIDATION.MISSING_SELECTED_DIGESTER',
  MISSING_AT_LEAST_ONE_UNIT = 'VALIDATION.MISSING_AT_LEAST_ONE_UNIT',
  MISSING_DEWATERING_TYPE = 'VALIDATION.MISSING_DEWATERING_TYPE',
  MISSING_AT_LEAST_ONE_PRODUCT = 'VALIDATION.MISSING_AT_LEAST_ONE_PRODUCT'
}

/**
 * An enum that contains the names of each validation section
 */
enum PlantSpecValidationSection {
  DIGESTER = 'PROJECT.PLANT_SPEC.DIGESTER',
  CHP = 'PROJECT.PLANT_SPEC.CHP',
  UPGRADER = 'PROJECT.PLANT_SPEC.UPGRADER',
  GENERATOR = 'PROJECT.PLANT_SPEC.GENERATOR',
  BOILER = 'PROJECT.PLANT_SPEC.BOILER',
  DEWATERING = 'PROJECT.PLANT_SPEC.DEWATERING',
  PRODUCTS = 'PROJECT.PLANT_SPEC.GEN_PROD',
  ADDITIONAL_PRODUCTS = 'PROJECT.PLANT_SPEC.ADDITIONAL_PRODUCTS',
  FUELS = 'PROJECT.PLANT_SPEC.CONS_FUELS'
}
